import { useState } from 'react';
import { formatPhoneNumberForStore } from '@lili/utils/formatting';
import { useBoundStore } from '@Store/index.ts';
import { OnboardingFormFields } from '@Types/index.ts';
import { useFormHook, useSetError } from '@Hooks/index';
import { phoneFormScheme } from '@Pages/happyFlowPages/phone/phoneForm/phoneFormScheme.ts';
import { useUpdatePhoneNumber } from '@Pages/happyFlowPages/phone/hooks/useUpdatePhoneNumber.tsx';
import { formatPhoneNumberForDefaultValue } from '@Services/utils/phoneNumber.ts';
import { RoutesMap } from '@Router/router.types';
import type { IPhoneForm } from '@Pages/happyFlowPages/phone/phoneForm/phoneForm.types.ts';
import type { RegionCode } from 'google-libphonenumber';

export const usePhoneLogic = (originRoute: RoutesMap = RoutesMap.PHONE) => {
  const getCustomerData = useBoundStore((state) => state.getCustomerData);
  const setCustomerData = useBoundStore((state) => state.setCustomerData);

  const {
    isNonUsSupported,
    customerData: { phoneNumber, phoneCountryCode, ssn },
  } = useBoundStore((state) => state.customer);

  const [localSelectedIsoCode, setLocalSelectedIsoCode] = useState<RegionCode>(
    phoneCountryCode || 'US'
  );

  const defaultValues: IPhoneForm = {
    [OnboardingFormFields.PHONE_NUMBER]: formatPhoneNumberForDefaultValue(phoneNumber || ''),
    [OnboardingFormFields.PHONE_NUMBER_VALIDATION_STATUS]: !!phoneNumber,
  };

  const formMethods = useFormHook({
    formSchema: phoneFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'phone-form',
  });

  const { mutateAsync: asyncUpdatePhone, isPending } = useUpdatePhoneNumber((errMsg) => {
    formMethods.setError(OnboardingFormFields.PHONE_NUMBER, { type: 'manual', message: errMsg });
  }, originRoute);

  const handleSubmit = async () => {
    const formData = formMethods.getValues();
    await asyncUpdatePhone({
      phoneNumber: formatPhoneNumberForStore(formData[OnboardingFormFields.PHONE_NUMBER]),
      isoCode: localSelectedIsoCode,
    });

    const customerToEdit = { ...getCustomerData() };
    customerToEdit.customerData.phoneCountryCode = localSelectedIsoCode;
    setCustomerData(customerToEdit);
  };

  useSetError({ formMethods });

  const isAuthUserPhone = originRoute === RoutesMap.AUTH_USER_PHONE;

  return {
    formMethods,
    isPending,
    handleSubmit,
    shouldAllowMultiCountry:
      (isAuthUserPhone || (originRoute === RoutesMap.PHONE && isNonUsSupported)) && !ssn,
    setLocalSelectedIsoCode,
  };
};
